<template>
  <b-modal
    id="purchaseItemForReferenceModal"
    no-close-on-backdrop
    size="xl"
    centered
    scrollable
    @hidden="onHideModal"
  >
    <template #modal-title>
      <p class="h4">
        {{ modalTitle }}
      </p>
    </template>

    <e-spinner v-if="loadingModal" />

    <div v-else>
      <!-- Filtros -->
      <e-filters
        ref="filters"
        :searching="fetching"
        class="mb-3"
        @search="onApplyFilters"
        @reset="onResetFilters"
      >
        <b-row>
          <b-col md="2">
            <FormulateInput
              id="purchase_order_id"
              v-model="filters.id"
              :type="getFixedPurchaseOrderId ? 'label' : 'number'"
              :label="$t('Id do pedido')"
              :instruction="getFixedPurchaseOrderId ? $t('Somente produtos deste pedido de compra podem ser referenciados') : ''"
              :router-to="getFixedPurchaseOrderId
                ? {
                  name: 'purchase-order-details',
                  params: {
                    id: getFixedPurchaseOrderId
                  },
                }
                : null"
            />
          </b-col>

          <b-col md="3">
            <e-person-search
              id="purchase_item_for_reference-supplier_id"
              v-model="filterInputs.supplierId"
              :label="$t('Pesquisar fornecedor')"
              is-supplier
              :only-active="false"
            />
          </b-col>

          <b-col md="3">
            <FormulateInput
              id="purchase_item_for_reference-invoice_number"
              v-model="filterInputs.invoiceNumber"
              type="number"
              :label="$t('Número da nota')"
            />
          </b-col>

          <b-col md="4">
            <FormulateInput
              id="purchase_item_for_reference-invoice_date"
              v-model="filterInputs.invoiceDate"
              type="date-range-picker"
              :label="$t('Período da NFe')"
              :time-picker="false"
              :class="getFixedPurchaseOrderId ? '' : 'required'"
              :validation="getFixedPurchaseOrderId ? '' : 'required'"
            />
          </b-col>
        </b-row>
      </e-filters>

      <!-- Tabela Aninhada com Paginação -->
      <b-card-actions
        no-actions
        class="mb-3"
      >
        <template #title>
          <div class="d-flex">
            <b-card-title>
              Pedidos de Compras
            </b-card-title>
            <e-instructions
              id="purchase_item_for_reference-title_instruction"
              class="ml-1"
              :instructions="[
                { text: $t('Selecione os itens do pedido de compra que serão referenciados') },
                !isMultipleOrders ? { text: $t('Só é possível referenciar itens de um único pedido de compra; se precisar referenciar outro, será necessário criar outra nota fiscal.') } : null,
              ]"
              :title="$t('Descrição')"
            />
          </div>
        </template>

        <b-row>
          <b-col md="12">
            <e-table-nested-rows
              :fields="fields"
              :items="purchaseOrdersWithSelection"
              nested-list-property="items"
              empty-text="Não foi encontrado produtos para referenciar"
              :fetching="fetching"
              :fixed-colapse="true"
              @nested-row-click="onItemSelectionChange"
            >
              <!-- Linha Master: Informações do Pedido de Compra -->
              <template #detail-cell(itemNumber)="{ item }">
                <span class="font-weight-bold">Pedido #{{ item.id }}</span>
              </template>

              <template #detail-cell(productName)="{ item }">
                <div class="d-flex gap-3">
                  <div class="d-flex flex-column">
                    <p class="mb-0">
                      NFe:
                    </p>
                    <p class="mb-0">
                      {{ item.invoiceKey }}
                    </p>
                  </div>

                  <div class="d-flex flex-column">
                    <p class="mb-0">
                      Fornecedor:
                    </p>
                    <p class="mb-0 font-weight-bold">
                      {{ item.supplier.document }} - {{ item.supplier.name }}
                    </p>
                  </div>
                </div>
              </template>

              <template #detail-cell(quantityEligibleForReturn)="{ item }">
                <div class="d-flex flex-column">
                  <p class="mb-0">
                    Data NFe:
                  </p>
                  <p class="mb-0">
                    {{ item.invoiceDate | date }}
                  </p>
                </div>
              </template>

              <template #detail-cell(unitMeasurement)="{ item }">
                <div class="d-flex flex-column">
                  <p class="mb-0">
                    Data entrada:
                  </p>
                  <p class="mb-0">
                    {{ item.completedDate | date('-') }}
                  </p>
                </div>
              </template>

              <!-- Linhas Aninhadas: Itens do Pedido de Compra -->
              <template #nested-cell(itemNumber)="{ item, itemNested }">
                <div
                  class="d-flex justify-content-center"
                  @click="(e) => e.stopPropagation()"
                >
                  <FormulateInput
                    :id="`purchase_item_for_reference-checkbox_item_${itemNested.id}`"
                    v-model="selectedItemsIdMap[itemNested.id]"
                    type="e-checkbox"
                    :disabled="Boolean(itemNested.quantityEligibleForReturn <= 0)"
                    @change="() => onItemSelectionChange(item, itemNested)"
                  />

                  <span class="font-weight-bold">#{{ itemNested.itemNumber }}</span>
                </div>
              </template>

              <template #nested-cell(productName)="{ itemNested }">
                <div>
                  <div class="font-weight-bold">
                    {{ itemNested.xmlProduct.name || itemNested.erpSku.sku.name }}
                  </div>
                </div>
              </template>

              <template #nested-cell(quantityEligibleForReturn)="{ itemNested }">
                <p class="font-weight-bold">
                  {{ itemNested.quantityEligibleForReturn || 0 | number(3, true) }}
                </p>
              </template>

              <template #nested-cell(unitMeasurement)="{ itemNested }">
                <div class="d-flex justify-content-center">
                  {{ itemNested.erpSku.sku.unitOfMeasurement }}
                </div>
              </template>

              <template #nested-cell(cfop)="{ itemNested }">
                {{ itemNested.cfop }}
              </template>
            </e-table-nested-rows>
          </b-col>
        </b-row>

        <!-- Paginação -->
        <b-row class="align-items-center">
          <b-col cols="6">
            <e-pagination-summary
              :current-page="paging.currentPage"
              :per-page="paging.pageSize"
              :total="paging.rowCount"
              :total-on-page="paging.rowsInCurrentPage"
            />
          </b-col>
          <b-col cols="6">
            <b-pagination
              :value="paging.currentPage"
              align="right"
              :total-rows="paging.rowCount"
              :per-page="paging.pageSize"
              @change="pageChange"
            />
          </b-col>
        </b-row>
      </b-card-actions>
    </div>

    <!-- Modal Footer com Ações -->
    <template #modal-footer>
      <b-row class="w-100 d-flex justify-content-between align-items-center">
        <b-col>
          <div>
            <p class="font-weight-bold mb-0">
              Itens Selecionados: {{ selectedItemsData.length }}
            </p>
          </div>
        </b-col>
        <b-col class="d-flex justify-content-end gap-2">
          <e-button
            variant="secondary"
            :text="$t('Cancelar')"
            @click="onHideModal"
          />
          <e-button
            variant="primary"
            :text="$t('Confirmar')"
            :disabled="getMissingQuantity > 0"
            :busy="saving"
            @click="onConfirm"
          >
            <template #right-content>
              <span v-if="getMissingQuantity > 0">
                ( Faltam: {{ getMissingQuantity }} )
              </span>
            </template>
          </e-button>
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>

<script>
import { BModal, BRow, BCol, BPagination, BCardTitle, } from 'bootstrap-vue'
import { mapActions, mapState } from 'vuex'
import ESpinner from '@/views/components/ESpinner.vue'
import EButton from '@/views/components/EButton.vue'
import ETableNestedRows from '@/views/components/tables/ETableNestedRows.vue'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'
import { alerts } from '@/mixins'
import EFilters from '@/views/components/EFilters.vue'
import EPaginationSummary from '@/views/components/pagination/EPaginationSummary.vue'
import EPersonSearch from '@/views/components/inputs/EPersonSearch.vue'
import { EInstructions } from '@/views/components'
import { getDefaultPurchaseItemForReferenceFilter } from '@/store/pages/purchase/purchase-order/purchase-order-for-invoice-reference'

const getDefaultItemData = () => ({
  skuId: null,
  sku: { name: null },
  invoiceItemId: null,
  quantity: 0,
  localId: null,
})

export default {
  components: {
    BModal,
    BRow,
    BCol,
    BPagination,
    ESpinner,
    EButton,
    ETableNestedRows,
    EFilters,
    EPaginationSummary,
    BCardActions,
    EPersonSearch,
    BCardTitle,
    EInstructions
  },

  mixins: [alerts],

  props: {
    isMultipleOrders: {
      type: Boolean,
      default: false
    },
    purchaseOrderIds: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      storeId: null,
      baseItem: null,
      modalTitle: '',
      loadingModal: false,
      saving: false,
      selectedItemsIdMap: {}, // Armazena os ids para usar nos checkboxes
      selectedItemsData: [], // Armazena os dados dos itens selecionados
      filterInputs: getDefaultPurchaseItemForReferenceFilter(),
      fetching: false,
    }
  },

  computed: {
    ...mapState('pages/purchase/purchaseOrder/purchaseOrderForInvoiceReference', {
      purchaseOrdersForInvoiceReference: 'purchaseOrdersForInvoiceReference',
      filters: 'filters',
      paging: 'paging',
    }),

    getFixedPurchaseOrderId() {
      return !this.isMultipleOrders && this.purchaseOrderIds.length > 0 ? this.purchaseOrderIds[0] : null
    },

    purchaseOrdersWithSelection() {
      return this.purchaseOrdersForInvoiceReference.map(po => ({
        ...po,
        isRowSelected: true,
      }))
    },

    getTotalQuantitySelected() {
      return this.selectedItemsData.reduce((prev, curr) => prev + (curr.quantityEligibleForReturn || 0), 0)
    },
    getMissingQuantity() {
      const result = (this.baseItem.quantity || 0) - this.getTotalQuantitySelected

      return result < 0 ? 0 : result
    },

    fields() {
      return [
        {
          label: this.$t('Nº do Item'),
          key: 'itemNumber',
          thStyle: { width: '100px' },
          detailTdStyle: {
            fontWeight: 'bold',
            backgroundColor: '#f3f2f7',
            textAlign: 'center'
          },
        },
        {
          label: this.$t('Produto'),
          key: 'productName',
          detailTdStyle: { fontWeight: 'bold', backgroundColor: '#f3f2f7' },
        },
        {
          label: this.$t('Qtd. da entrada disponível'),
          key: 'quantityEligibleForReturn',
          thStyle: { width: '150px' },
          detailTdStyle: { fontWeight: 'bold', backgroundColor: '#f3f2f7' },
          tdStyle: { textAlign: 'center' }
        },
        {
          label: this.$t('Unid.'),
          key: 'unitMeasurement',
          thStyle: { width: '120px' },
          detailTdStyle: { fontWeight: 'bold', backgroundColor: '#f3f2f7' },
        },
        {
          label: 'CFOP',
          key: 'cfop',
          thStyle: { width: '80px' },
          detailTdStyle: { fontWeight: 'bold', backgroundColor: '#f3f2f7' },
          tdStyle: { textAlign: 'center' }
        },
      ]
    },
  },

  methods: {
    ...mapActions('pages/purchase/purchaseOrder/purchaseOrderForInvoiceReference', {
      stFetchOrderPurchaseForInvoiceReference: 'fetchOrderPurchaseForInvoiceReference',
      stResetFilters: 'resetFilters',
      stSetCurrentPage: 'setCurrentPage',
      stSetFilters: 'setFilters',
      stCleanState: 'cleanState',
    }),

    async show(storeId, pItemData) {
      const itemData = {
        ...getDefaultItemData(),
        ...pItemData
      }

      if (!storeId) {
        this.showWarning({
          title: this.$t('Loja não selecionada'),
          message: this.$t('Selecione a loja para referenciar item no pedido de compra.'),
        })
        return
      }

      if (!itemData?.skuId) {
        this.showWarning({
          title: this.$t('Produto não selecionado'),
          message: this.$t('Selecione o produto para referenciar item no pedido de compra..')
        })
        return
      }

      this.$bvModal.show('purchaseItemForReferenceModal')

      try {
        this.resetState()

        this.loadingModal = true
        this.storeId = storeId
        this.baseItem = itemData

        const productName = itemData.sku?.name
        this.modalTitle = this.$t(`Referenciar item para ${productName}`)

        // Inicializa os inputs de filtro com os valores do store
        this.filterInputs = {
          ...getDefaultPurchaseItemForReferenceFilter(),
        }

        if (this.getFixedPurchaseOrderId) {
          this.filterInputs.id = this.getFixedPurchaseOrderId
          this.filterInputs.invoiceDate = {}
        }

        await this.onApplyFilters()
      } catch (error) {
        this.showError({ error })
      } finally {
        this.loadingModal = false
      }
    },

    async getData() {
      if (!this.filterInputs.invoiceDate?.startDate && !this.getFixedPurchaseOrderId) {
        this.showInvalidDataMessage()
        return
      }

      try {
        this.fetching = true

        // Busca os dados
        await this.stFetchOrderPurchaseForInvoiceReference({
          storeId: this.storeId,
          skuId: this.baseItem.skuId
        })
      } catch (error) {
        this.showError({ error })
      } finally {
        this.fetching = false
      }
    },

    async onApplyFilters() {
      // Atualiza os filtros no store com os valores dos inputs
      this.stSetCurrentPage(1)
      await this.stSetFilters(this.filterInputs)

      // Busca os dados
      await this.getData()
    },

    async onResetFilters() {
      await this.stResetFilters()
      this.filterInputs = getDefaultPurchaseItemForReferenceFilter()

      await this.onApplyFilters()
    },

    async pageChange(newPage) {
      this.stSetCurrentPage(newPage)

      await this.getData()
    },

    onItemSelectionChange(item, childItem) {
      const purchaseOrderItemId = childItem.id
      const alreadySelected = this.selectedItemsData.some(i => i.purchaseOrderItemId === purchaseOrderItemId)

      if (alreadySelected) {
        this.removeItemSelected(purchaseOrderItemId)
      } else {
        if (childItem.quantityEligibleForReturn <= 0) {
          this.showWarning({ message: this.$t('Todas as unidades já foram referenciadas.') })
          this.removeItemSelected(purchaseOrderItemId)
          return
        }

        if (this.getMissingQuantity <= 0) {
          this.showWarning({ message: this.$t('Já foi selecionado a quantidade necessária para referenciar.') })
          this.removeItemSelected(purchaseOrderItemId)
          return
        }

        const diffLocalOrder = this.selectedItemsData.filter(i => i.purchaseOrderId !== item.id)
        const diffOrderIds = (this.purchaseOrderIds || []).filter(id => id !== item.id)
        if (!this.isMultipleOrders && (diffLocalOrder.length > 0 || diffOrderIds.length > 0)) {
          const orderId = diffOrderIds[0] || diffLocalOrder[0].purchaseOrderId
          this.showWarning({
            title: this.$t(`Pedido #${orderId} já foi referenciado.`),
            message: this.$t("Somente itens de 1 pedido de compra pode ser referenciado.")
          })
          this.removeItemSelected(purchaseOrderItemId)
          return
        }

        this.addItemSelected(item, childItem)
      }
    },

    addItemSelected(purchaseOrder, purchaseOrderItem) {
      const itemBody = {
        ...purchaseOrderItem,
        purchaseOrderItemId: purchaseOrderItem.id,
        purchaseOrderId: purchaseOrder.id,
        purchaseOrderInvoiceKey: purchaseOrder.invoiceKey,
        // usado para buscar invoiceItem no array do state da invoice-maintain
        invoiceItem: {
          id: this.baseItem.invoiceItemId,
          localId: this.baseItem.localId,
        }
      }

      this.selectedItemsIdMap = {
        ...this.selectedItemsIdMap,
        [itemBody.purchaseOrderItemId]: true
      }
      this.selectedItemsData = [...this.selectedItemsData, itemBody]
    },
    removeItemSelected(orderItemId) {
      const mapped = { ...this.selectedItemsIdMap }
      delete mapped[orderItemId]
      this.selectedItemsIdMap = mapped

      this.selectedItemsData = this.selectedItemsData.filter(
        sel => sel.purchaseOrderItemId !== orderItemId
      )
    },
    resetItemSelected() {
      this.selectedItemsIdMap = {}
      this.selectedItemsData = []
    },

    onHideModal() {
      this.$bvModal.hide('purchaseItemForReferenceModal')
    },

    resetState() {
      this.storeId = null
      this.baseItem = null
      this.resetItemSelected()
    },

    async onConfirm() {
      try {
        this.saving = true

        if (this.selectedItemsData.length === 0) {
          this.showWarning({ message: this.$t('Selecione pelo menos um item') })
          return
        }

        // Emite os itens selecionados para o componente pai
        this.$emit('items-selected', this.selectedItemsData, this.baseItem)

        this.onHideModal()
      } catch (error) {
        this.showError({ error })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped></style>
